import { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, post, put, del, can } from "@/utils/service";
import { apiMessage, label, shortDate } from "@/utils/api";
import { Panel, Field, input, primary, secondary, danger } from "./shared";

interface Subscription { id: string; url: string; events: string[]; is_active: boolean; failure_count: number; last_delivered_at: string | null }
interface Delivery { id: string; event: string; status: string; response_status: number | null; error: string | null; created_at: string }
interface Available { key: string; label: string; methods: string[] }
interface Connection { id: string; provider: string; name: string; status: string; has_credentials: boolean; last_used_at: string | null; last_error: string | null }

/** Webhooks to your own systems, and connections to payment providers. */
export default function Integrations() {
  const editable = can("integrations.manage");
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [available, setAvailable] = useState<Available[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [draft, setDraft] = useState<{ url: string; events: string[] } | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<{ id: string; rows: Delivery[] } | null>(null);

  const load = () => Promise.all([
    get<{ data: { subscriptions: Subscription[]; events: string[] } }>("/webhooks").then((r) => { setSubs(r.data.subscriptions); setEvents(r.data.events); }),
    get<{ data: { available: Available[]; connections: Connection[] } }>("/integrations").then((r) => { setAvailable(r.data.available); setConnections(r.data.connections); }),
  ]).catch((e) => notify(apiMessage(e, "Could not load integrations.")));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft) return;
    try {
      const r = await post<{ data: { secret: string } }>("/webhooks", draft);
      setSecret(r.data.secret);
      setDraft(null);
      load();
    } catch (e) { notify(apiMessage(e, "Could not add the webhook.")); }
  };
  const toggle = async (s: Subscription) => {
    try { await put(`/webhooks/${s.id}`, { is_active: !s.is_active }); load(); }
    catch (e) { notify(apiMessage(e, "Could not update the webhook.")); }
  };
  const remove = async (s: Subscription) => {
    if (!await confirmDialog(`Stop sending events to ${s.url}?`)) return;
    try { await del(`/webhooks/${s.id}`); load(); }
    catch (e) { notify(apiMessage(e, "Could not delete the webhook.")); }
  };
  const showDeliveries = async (s: Subscription) => {
    const r = await get<{ data: { deliveries: Delivery[] } }>(`/webhooks/${s.id}/deliveries`).catch(() => null);
    setDeliveries({ id: s.id, rows: r?.data.deliveries ?? [] });
  };

  return (
    <div className="space-y-6">
      <Panel title="Webhooks" description="Send signed notifications to your own systems when things happen (a deal is won, an invoice is issued…)" actions={editable && !draft && <button onClick={() => setDraft({ url: "https://", events: [] })} className={secondary}>Add webhook</button>}>
        {secret && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-semibold">Copy the signing secret now. It won't be shown again.</p>
            <div className="mt-2 flex items-center gap-2"><code className="flex-1 break-all rounded bg-white px-2 py-1">{secret}</code><button onClick={() => { navigator.clipboard.writeText(secret); notify("Copied"); }} className={secondary}><Copy className="w-3.5 h-3.5" /></button></div>
            <p className="mt-2">Each request carries an <code>X-Webhook-Signature</code> header: <code>t=&lt;time&gt;,v1=&lt;HMAC-SHA256 of "time.body"&gt;</code>.</p>
            <button onClick={() => setSecret(null)} className="mt-2 underline">I've saved it</button>
          </div>
        )}
        {draft && (
          <div className="mb-6 space-y-4 rounded-lg border border-gray-200 p-4">
            <Field label="Endpoint URL" hint="Must be a public https address"><input className={input} value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} /></Field>
            <Field label="Events">
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                <label className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" className="accent-primary" checked={draft.events.includes("*")} onChange={(e) => setDraft({ ...draft, events: e.target.checked ? ["*"] : [] })} />All events</label>
                {!draft.events.includes("*") && events.map((ev) => (
                  <label key={ev} className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" className="accent-primary" checked={draft.events.includes(ev)} onChange={(e) => setDraft({ ...draft, events: e.target.checked ? [...draft.events, ev] : draft.events.filter((x) => x !== ev) })} />{ev}</label>
                ))}
              </div>
            </Field>
            <div className="flex justify-end gap-2"><button onClick={() => setDraft(null)} className={secondary}>Cancel</button><button onClick={create} disabled={!draft.url.startsWith("https://") || draft.events.length === 0} className={primary}>Add webhook</button></div>
          </div>
        )}
        {subs.length === 0 ? <p className="text-xs text-gray-500">No webhooks configured.</p> : (
          <div className="grid gap-4 lg:grid-cols-2">
            {subs.map((s) => {
              const health = !s.is_active ? { dot: "bg-gray-400", text: "Paused" } : s.failure_count > 0 ? { dot: "bg-amber-500", text: `${s.failure_count} failed in a row` } : { dot: "bg-green-500", text: "Healthy" };
              return (
                <div key={s.id} className="flex flex-col rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${health.dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900" title={s.url}>{s.url.replace(/^https:\/\//, "")}</p>
                      <p className="text-xs text-gray-500">{health.text}{s.last_delivered_at ? ` · last delivered ${shortDate(s.last_delivered_at)}` : " · nothing delivered yet"}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {s.events.includes("*") ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">All events</span>
                      : s.events.map((ev) => <span key={ev} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{ev}</span>)}
                  </div>
                  {deliveries?.id === s.id && (
                    <div className="mt-3 max-h-48 overflow-y-auto rounded-lg bg-gray-50 p-3 text-xs">
                      {deliveries.rows.length === 0 ? <p className="text-gray-500">Nothing sent yet.</p> : deliveries.rows.slice(0, 20).map((d) => (
                        <p key={d.id} className="flex justify-between gap-3 py-0.5"><span>{d.event}</span><span className={d.status === "delivered" ? "text-green-700" : d.status === "failed" ? "text-red-600" : "text-gray-500"}>{label(d.status)}{d.response_status ? ` (${d.response_status})` : ""} · {shortDate(d.created_at)}</span></p>
                      ))}
                    </div>
                  )}
                  <div className="min-h-[1rem] flex-1" />
                  <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                    <button className={secondary} onClick={() => (deliveries?.id === s.id ? setDeliveries(null) : showDeliveries(s))}>{deliveries?.id === s.id ? "Hide deliveries" : "Deliveries"}</button>
                    {editable && <button className={secondary} onClick={() => toggle(s)}>{s.is_active ? "Pause" : "Resume"}</button>}
                    {editable && <button className={`${danger} ml-auto`} onClick={() => remove(s)}>Delete</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel title="Payment Providers" description="Collect mobile money or card payments online and have invoices marked paid automatically">
        {available.length === 0 ? (
          <p className="text-xs text-gray-500">No online payment provider is installed on this system yet. Payments are recorded manually from invoices or the Payments page. When a provider is added, you'll connect it here with your own account credentials.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {available.map((a) => {
              const c = connections.find((x) => x.provider === a.key);
              return (
                <div key={a.key} className="flex items-center justify-between py-3">
                  <div><p className="text-xs font-semibold text-gray-900">{a.label}</p><p className="text-xs text-gray-500">{c ? `${label(c.status)}${c.last_error ? ` · ${c.last_error}` : ""}` : `Accepts ${a.methods.map(label).join(", ")}`}</p></div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
